import { ensureDefined } from "common/utils/assertOfType";

/**
 * Functions for extruding geometry both vectors and PolyLine3 paths
 * @module
 */
import type { AABB3 } from "./aabb3Types";
import type { BRep, Extrusion, Face, FaceTagBase, FaceTopo } from "./brep";
import { brepNew, faceOrNullCopy, faceOrNullMove, faceNew, faceOrNullReverse, faceTopoNew, facesAABB3 } from "./brep";
import { DIST_EPSILON, ZERO_EPSILON } from "./core";
import {
  planeByThreePoints,
  planeCopy,
  planeEval,
  planeNew,
  planeNewXY,
  planeNormal,
  planeProject,
  planeProjectPolyLine3,
  planeRotateAroundNormal,
} from "./plane";
import type { Plane } from "./planeTypes";
import {
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2MakeAxisAlignedRect,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
  polyLine2Rotate,
  polyLine2SetCW,
} from "./polyLine2";
import { polyLine2New, type PolyLine2 } from "./polyLine2Types";
import type { PolyLine3 } from "./polyLine3";
import { polyLine3EdgeCount, polyLine3EdgeTangent, polyLine3IsClosed, polyLine3New } from "./polyLine3";
import { rect2ByCenterWidthHeight } from "./rect2";
import { vec2Dist } from "./vec2";
import type { Vec3 } from "./vec3";
import {
  vec3Add,
  vec3Cross,
  vec3CrossNormalize,
  vec3Dist,
  vec3Dot,
  vec3Len,
  vec3Normalize,
  vec3Scale,
  vec3Sub,
} from "./vec3";

const { sqrt, abs } = Math;

export function extrusionToBRep({ profile: face, dirX, dirY, dirZ }: Extrusion): BRep {
  const faces: Face[] = [];
  const faceTopos: FaceTopo[] = [];
  const brep: BRep = brepNew(faces, faceTopos);

  // this function handles making topo for the top and bottom face

  function buildCapTopo(top: boolean) {
    const sideFaceEdgeIndex = top ? 2 : 0;

    const faceTopo = faceTopoNew();
    faceTopo.loopTopos = bottomFace ? new Array<Int32Array>(bottomFace.loops.length) : [];

    let runningFaceCount = 2; // top and bottom cap

    for (let i = 0, li = face.loops.length; i < li; i++) {
      const loop = face.loops[i];
      if (loop.length === 0) {
        continue;
      }
      const loopTopos = (faceTopo.loopTopos[i] = new Int32Array(polyLine2EdgeCount(loop) * 3));

      for (let j = 0, lj = loopTopos.length; j < lj; j += 3) {
        loopTopos[j] = runningFaceCount;
        loopTopos[j + 1] = 0; // the side faces have a single outer loop
        loopTopos[j + 2] = sideFaceEdgeIndex;
        runningFaceCount++;
      }
    }

    return faceTopo;
  }

  // bottom geo

  const bottomFace = faceOrNullCopy(face);
  faceOrNullReverse(bottomFace);
  const bottomFaceIndex = faces.length;

  if (bottomFace) {
    faces.push(bottomFace);
  }

  const bottomFaceTopo = buildCapTopo(false);
  faceTopos.push(bottomFaceTopo);

  // top

  const topFace = faceOrNullCopy(face);
  faceOrNullMove(topFace, dirX, dirY, dirZ);
  const topFaceIndex = faces.length;

  if (topFace) {
    faces.push(topFace);
  }

  const topFaceTopo = buildCapTopo(true);
  faceTopos.push(topFaceTopo);

  // sides

  const [dirNormX, dirNormY, dirNormZ] = vec3Normalize(dirX, dirY, dirZ);
  const len = vec3Dist(0, 0, 0, dirX, dirY, dirZ);

  for (let i = 0; i < face.loops.length; i++) {
    const loop = face.loops[i];
    const loopFaceOffset = faces.length; // start index of faces from this loop
    for (let j = 0, l = loop.length - 2; j < l; j += 2) {
      extrudeEdge(
        face,
        dirNormX,
        dirNormY,
        dirNormZ,
        i,
        j,
        loopFaceOffset,
        bottomFaceIndex,
        topFaceIndex,
        faces,
        faceTopos,
        len,
        len,
      );
    }
  }

  return brep;
}

export function extrudeFaceAlongNormal(face: Face, distance: number) {
  let [faceNormX, faceNormY, faceNormZ] = planeNormal(face.plane);
  [faceNormX, faceNormY, faceNormZ] = vec3Scale(faceNormX, faceNormY, faceNormZ, distance);

  return extrudeFace(face, faceNormX, faceNormY, faceNormZ);
}

export function extrudeFace(face: Face, dirX: number, dirY: number, dirZ: number): Extrusion {
  const [faceNormX, faceNormY, faceNormZ] = planeNormal(face.plane);
  const faceDirDot = vec3Dot(dirX, dirY, dirZ, faceNormX, faceNormY, faceNormZ);

  // fail if extrusion direction is in plane
  if (!(abs(faceDirDot) > DIST_EPSILON)) {
    console.assert(false, "Extrusion direction is in the plane of the face");
  }

  // if the extrusion is not in the direction of the face normal, we create a reverse copy
  if (faceDirDot < 0) {
    const copy = faceOrNullCopy(face);
    face = copy ?? face;
    faceOrNullReverse(face);
  }

  return { type: "EXTRUSION", profile: face, dirX, dirY, dirZ };
}

export function extrudePolyLine2(pl: PolyLine2, plane: Plane, dirX: number, dirY: number, dirZ: number): Extrusion {
  // defensively copy and ensure the profile has the correct direction
  pl = polyLine2Copy(pl);
  polyLine2SetCW(pl);
  return extrudeFace(faceNew([pl], plane), dirX, dirY, dirZ);
}

/**
 * Extrudes a PolyLine2 after rotating its plane basis, preserving the profile's local coordinate axes.
 */
export function extrudePolyLine2RotatedBasis(
  pl: PolyLine2,
  plane: Plane,
  dirX: number,
  dirY: number,
  dirZ: number,
  rotation: number,
): Extrusion {
  const rotatedPlane = planeCopy(plane);
  planeRotateAroundNormal(rotatedPlane, rotation);
  return extrudePolyLine2(pl, rotatedPlane, dirX, dirY, dirZ);
}

export function extrudePolyLine2AlongNormal(pl: PolyLine2, plane: Plane, dist: number): Extrusion {
  const [nx, ny, nz] = planeNormal(plane);

  // defensively copy and ensure the profile has the correct direction
  pl = polyLine2Copy(pl);
  polyLine2SetCW(pl);
  return extrudeFace(faceNew([pl], plane), nx * dist, ny * dist, nz * dist);
}

export function extrudePolyLine2InZ(profile: PolyLine2, z: number, dist: number): Extrusion {
  return extrudePolyLine2(profile, planeNewXY(0, 0, z), 0, 0, dist);
}

/**
 * Extrudes only in Z but can be extended to other dimensions if needed
 * The volume generated should not be different as it is a rectangular prism
 */
export function extrudeAxisAlignedBoxFromAABB3(
  aabb: AABB3,
  clampMinExtrudeZ: number = 2 * DIST_EPSILON,
): Extrusion | undefined {
  const { minX, minY, minZ, maxX, maxY, maxZ } = aabb;
  const dz = maxZ - minZ;
  const dx = maxX - minX;
  const dy = maxY - minY;

  if (dz <= ZERO_EPSILON || dx <= ZERO_EPSILON || dy <= ZERO_EPSILON) {
    return undefined;
  }

  const profile = polyLine2MakeAxisAlignedRect(minX, minY, maxX, maxY);

  const extrudeZ = Math.max(dz, clampMinExtrudeZ);

  return extrudePolyLine2InZ(profile, minZ, extrudeZ);
}

// unlike extrudePolyLine2, extrudePolyLine2FacesOnPlane does not assume the polyline is closed
// and will not create "cap" faces
export function extrudePolyLine2OnPlane(plane: Plane, path: PolyLine2, distance: number): Face[] {
  const extruded: Face[] = [];

  const [nx, ny, nz] = planeNormal(plane);
  const [dx, dy, dz] = vec3Scale(nx, ny, nz, distance);

  for (let i = 0; i < path.length - 2; i += 2) {
    const extrudedEdge = extrudeLine2(plane, path[i], path[i + 1], path[i + 2], path[i + 3], dx, dy, dz);
    if (extrudedEdge) {
      extruded.push(extrudedEdge);
    }
  }

  return extruded;
}

export function extrudePolyLine2sOnPlane(plane: Plane, paths: PolyLine2[], distance: number): Face[][] {
  return paths.map((path) => extrudePolyLine2OnPlane(plane, path, distance));
}

export function extrudeEdge(
  face: Face,
  dirX: number,
  dirY: number,
  dirZ: number,
  loopIndex: number,
  edgeIndex: number,
  loopFaceOffset: number,
  bottomFaceIndex: number,
  topFaceIndex: number,
  faces: Face[],
  faceTopos: FaceTopo[],
  dist0: number,
  dist1: number,
) {
  const loop = face.loops[loopIndex];
  const basePlane = face.plane;
  const baseOuterLoopEdgeCount = polyLine2EdgeCount(face.loops[0]);

  const u0 = loop[edgeIndex];
  const v0 = loop[edgeIndex + 1];
  const u1 = loop[edgeIndex + 2];
  const v1 = loop[edgeIndex + 3];

  // alloc and init the plane for the face
  const plane = planeNew();

  const [globalX0, globalY0, globalZ0] = planeEval(basePlane, u0, v0);
  const [globalX1, globalY1, globalZ1] = planeEval(basePlane, u1, v1);

  const [edgeDirX, edgeDirY, edgeDirZ] = vec3Sub(globalX0, globalY0, globalZ0, globalX1, globalY1, globalZ1);
  const [newFaceUX, newFaceUY, newFaceUZ] = vec3Normalize(edgeDirX, edgeDirY, edgeDirZ);

  plane.originX = globalX1;
  plane.originY = globalY1;
  plane.originZ = globalZ1;

  // this is reversed because the direction of the loop is
  // cw

  plane.uX = newFaceUX;
  plane.uY = newFaceUY;
  plane.uZ = newFaceUZ;

  const [newFaceNormX, newFaceNormY, newFaceNormZ] = vec3Cross(edgeDirX, edgeDirY, edgeDirZ, dirX, dirY, dirZ);
  let [newFaceVX, newFaceVY, newFaceVZ] = vec3Cross(
    newFaceNormX,
    newFaceNormY,
    newFaceNormZ,
    newFaceUX,
    newFaceUY,
    newFaceUZ,
  );
  [newFaceVX, newFaceVY, newFaceVZ] = vec3Normalize(newFaceVX, newFaceVY, newFaceVZ);

  plane.vX = newFaceVX;
  plane.vY = newFaceVY;
  plane.vZ = newFaceVZ;

  // alloc and init the outerLoop
  const pl = polyLine2New(10);

  // compute the length of the edge
  const du = u1 - u0;
  const dv = v1 - v0;

  const uLen = sqrt(du * du + dv * dv);

  const vLen = vec3Dot(newFaceVX, newFaceVY, newFaceVZ, dirX, dirY, dirZ) * dist0;
  const uDotDir = vec3Dot(newFaceUX, newFaceUY, newFaceUZ, dirX, dirY, dirZ) * dist1;

  pl[0] = 0;
  pl[1] = 0;

  pl[2] = uDotDir;
  pl[3] = vLen;

  pl[4] = uLen + uDotDir;
  pl[5] = vLen;

  pl[6] = uLen;
  pl[7] = 0;

  pl[8] = 0;
  pl[9] = 0;

  // face topology
  const loopEdgeCount = loop.length / 2 - 1;
  const trueEdgeIndex = edgeIndex / 2;

  const prevFaceIndex = loopFaceOffset + ((trueEdgeIndex - 1 + loopEdgeCount) % loopEdgeCount);
  const nextFaceIndex = loopFaceOffset + ((trueEdgeIndex + 1) % loopEdgeCount);

  const fa = faceTopoNew();
  fa.loopTopos = [
    new Int32Array([
      bottomFaceIndex,
      loopIndex,
      trueEdgeIndex,
      topFaceIndex,
      loopIndex,
      baseOuterLoopEdgeCount - loopIndex - 1, // the top loop is a reversed copy of the generating base loop
      prevFaceIndex,
      0,
      3, // the right edge of the outer loop of the prev face in this loop
      nextFaceIndex,
      0,
      1, // the left edge of the outer loop of the next face in this loop
    ]),
  ];

  const f = faceNew([pl], plane);

  faces.push(f);
  faceTopos.push(fa);
}

export function extrudeLine2(
  plane: Plane,
  startU: number,
  startV: number,
  endU: number,
  endV: number,
  dirX: number,
  dirY: number,
  dirZ: number,
): Face | null {
  const [originX, originY, originZ] = planeEval(plane, startU, startV);
  const [endX, endY, endZ] = planeEval(plane, endU, endV);

  const [topX, topY, topZ] = vec3Add(originX, originY, originZ, dirX, dirY, dirZ);

  const vlen = vec3Len(dirX, dirY, dirZ);
  const ulen = vec2Dist(startU, startV, endU, endV);
  const facePlane = planeByThreePoints(originX, originY, originZ, endX, endY, endZ, topX, topY, topZ);

  if (facePlane === null) {
    // console.assert(false, "invalid point set in planeByThreePoints");
    return null;
  }

  return faceNew([polyLine2New([0, 0, 0, vlen, ulen, vlen, ulen, 0, 0, 0])], facePlane);
}

export function extrudeLine3(
  startX: number,
  startY: number,
  startZ: number,
  endX: number,
  endY: number,
  endZ: number,
  dirX: number,
  dirY: number,
  dirZ: number,
): Face | null {
  const aX = startX;
  const aY = startY;
  const aZ = startZ;

  const bX = endX;
  const bY = endY;
  const bZ = endZ;

  const cX = bX + dirX;
  const cY = bY + dirY;
  const cZ = bZ + dirZ;

  const dX = aX + dirX;
  const dY = aY + dirY;
  const dZ = aZ + dirZ;

  const facePlane = planeByThreePoints(aX, aY, aZ, bX, bY, bZ, cX, cY, cZ);
  if (!facePlane) {
    return null;
  }

  const loop3 = polyLine3New([aX, aY, aZ, bX, bY, bZ, cX, cY, cZ, dX, dY, dZ, aX, aY, aZ]);
  const loop2 = planeProjectPolyLine3(facePlane, loop3);

  polyLine2SetCW(loop2);

  return faceNew([loop2], facePlane);
}

// there are two legal rotation planes to miter the joints
enum RotationPlane {
  XZ,
  YZ,
}

function getRotationPlane(dirx: number, diry: number, dirz: number, prevFrame: Plane) {
  const xcomp = vec3Dot(dirx, diry, dirz, prevFrame.uX, prevFrame.uY, prevFrame.uZ);
  const ycomp = vec3Dot(dirx, diry, dirz, prevFrame.vX, prevFrame.vY, prevFrame.vZ);

  const xzrot = abs(xcomp) > ZERO_EPSILON;
  const yzrot = abs(ycomp) > ZERO_EPSILON;

  if (xzrot && yzrot) {
    return null;
  }

  return xzrot ? RotationPlane.XZ : RotationPlane.YZ;
}

function buildFacesBetweenEdges(
  profile: PolyLine2[],
  startPlane: Plane,
  endPlane: Plane,
  faces: Face[],
  faceTopos: FaceTopo[],
  tags: readonly FaceTagBase[] | undefined = undefined,
) {
  // the input is cw, but at this point we've reversed and the loop is ccw
  for (let i = 0, l = profile.length; i < l; i++) {
    const loop = profile[i];

    // build quadrilateral faces on each edge of the extrusion
    // the loop is defined by points a, b, c, d, which are selected from
    // the edges on the two planes

    // evaluate the first point on the loop at each plane
    let [ax, ay, az] = planeEval(startPlane, loop[0], loop[1]);
    let [dx, dy, dz] = planeEval(endPlane, loop[0], loop[1]);

    for (let j = 0, jl = loop.length - 2; j < jl; j += 2) {
      // evaluate the next point on the loop at each plane
      const [bx, by, bz] = planeEval(startPlane, loop[j + 2], loop[j + 3]);
      const [cx, cy, cz] = planeEval(endPlane, loop[j + 2], loop[j + 3]);

      // In some cases, points defining the loop can coincide, causing planeByThreePoints to fail.
      // If both the current and next point on the loop coincide, the face is completely degenerate
      // and is skipped. If the next points are not coincident, we use two of those and the original
      // point to create the plane.
      let plane = planeByThreePoints(ax, ay, az, dx, dy, dz, bx, by, bz);

      if (!plane) {
        // Plane creation fails because a == d. Attempt again with c instead of d.
        plane = planeByThreePoints(ax, ay, az, cx, cy, cz, bx, by, bz);
      }

      if (plane) {
        const [bu, bv] = planeProject(plane, bx, by, bz);
        const [cu, cv] = planeProject(plane, cx, cy, cz);
        const [du, dv] = planeProject(plane, dx, dy, dz);

        const pl = polyLine2New(10);

        pl[0] = 0;
        pl[1] = 0;

        pl[2] = bu;
        pl[3] = bv;

        pl[4] = cu;
        pl[5] = cv;

        pl[6] = du;
        pl[7] = dv;

        pl[8] = 0;
        pl[9] = 0;

        faces.push(faceNew([pl], plane, tags ? [...tags] : undefined));
        faceTopos.push(faceTopoNew());
      }

      ax = bx;
      ay = by;
      az = bz;

      dx = cx;
      dy = cy;
      dz = cz;
    }
  }
}

export function extrudeFaceAlongPolyLine3OnPlane(
  profile: PolyLine2[],
  path: PolyLine3,
  plane: Plane,
  startTangent: Vec3 | null = null,
  endTangent: Vec3 | null = null,
) {
  const [nx, ny, nz] = planeNormal(plane);
  const [dirx, diry, dirz] = startTangent ?? polyLine3EdgeTangent(path, 0);
  const [upx, upy, upz] = vec3CrossNormalize(dirx, diry, dirz, nx, ny, nz);

  return extrudeFaceAlongPolyLine3(profile, path, upx, upy, upz, startTangent, endTangent);
}

// the profile curves are a set, the first curve should be CW and the rest CCW
export function extrudeFaceAlongPolyLine3(
  profile: PolyLine2[],
  path: PolyLine3,
  upx: number,
  upy: number,
  upz: number,
  startTangent: Vec3 | null = null,
  endTangent: Vec3 | null = null,
  pathSegmentFaceTags: readonly (readonly FaceTagBase[])[] | undefined = undefined,
): BRep {
  if (!path) {
    return brepNew([], []);
  }

  const pathClosed = polyLine3IsClosed(path);
  const pathLen = path.length;
  const pathNumEdges = polyLine3EdgeCount(path);
  if (!(pathSegmentFaceTags === undefined || pathSegmentFaceTags.length === pathNumEdges)) {
    console.assert(false, "Path segment face tags must match the number of path edges");
  }

  const newProfile = new Array(profile.length);

  for (let i = 0, li = profile.length; i < li; i++) {
    const profileCopy = polyLine2Copy(profile[i]);
    polyLine2ReflectOverYAxis(profileCopy);
    polyLine2Reverse(profileCopy);
    newProfile[i] = profileCopy;
  }

  profile = newProfile;

  // 1. build edge frames - frenet frames aligned with edges
  const edgeFrames = new Array<Plane>(pathNumEdges);
  const vertexRotationPlanes = new Array<RotationPlane | null>(pathNumEdges);

  // build initial normal plane
  const plane0 = planeNew();
  edgeFrames[0] = plane0;

  {
    const [dirx, diry, dirz] = startTangent ?? polyLine3EdgeTangent(path, 0);
    // orthonormalize
    const [ux, uy, uz] = vec3CrossNormalize(upx, upy, upz, dirx, diry, dirz);
    const [vx, vy, vz] = vec3CrossNormalize(dirx, diry, dirz, ux, uy, uz);
    if (Number.isNaN(ux) || Number.isNaN(vx)) {
      return brepNew([], []);
    }

    plane0.originX = path[0];
    plane0.originY = path[1];
    plane0.originZ = path[2];

    plane0.uX = ux;
    plane0.uY = uy;
    plane0.uZ = uz;

    plane0.vX = vx;
    plane0.vY = vy;
    plane0.vZ = vz;
  }

  const lastI = pathLen - 6;
  for (let i = 3; i < pathLen - 3; i += 3) {
    const i3 = i / 3;
    const prevFrame = edgeFrames[i3 - 1];
    let [dirx, diry, dirz] = polyLine3EdgeTangent(path, i3); // direction of edge emanating from this vertex

    if (i === lastI && endTangent) {
      [dirx, diry, dirz] = endTangent;
    }

    const rotplane = getRotationPlane(dirx, diry, dirz, prevFrame);

    if (!(rotplane !== null)) {
      console.assert(false, "Path twists at vertex " + i);
    }

    const plane = planeNew();
    edgeFrames[i3] = plane;

    plane.originX = path[i + 0];
    plane.originY = path[i + 1];
    plane.originZ = path[i + 2];

    vertexRotationPlanes[i3] = rotplane;

    if (rotplane === RotationPlane.XZ) {
      plane.vX = prevFrame.vX;
      plane.vY = prevFrame.vY;
      plane.vZ = prevFrame.vZ;

      const [ux, uy, uz] = vec3Cross(prevFrame.vX, prevFrame.vY, prevFrame.vZ, dirx, diry, dirz);
      plane.uX = ux;
      plane.uY = uy;
      plane.uZ = uz;
    } else {
      plane.uX = prevFrame.uX;
      plane.uY = prevFrame.uY;
      plane.uZ = prevFrame.uZ;

      const [vx, vy, vz] = vec3Cross(dirx, diry, dirz, prevFrame.uX, prevFrame.uY, prevFrame.uZ);
      plane.vX = vx;
      plane.vY = vy;
      plane.vZ = vz;
    }
  }

  if (pathClosed) {
    const prevFrame = edgeFrames[edgeFrames.length - 1];
    const [dirx, diry, dirz] = polyLine3EdgeTangent(path, 0); // direction of edge emanating from this vertex
    const rotplane = getRotationPlane(dirx, diry, dirz, prevFrame);

    if (!(rotplane !== null)) {
      console.assert(false, "Path twists at vertex 0");
    }

    vertexRotationPlanes[0] = ensureDefined(rotplane);
  }

  // 2. build miter frames - frames that turn along the path

  const miterPlanes = new Array<Plane>(pathNumEdges);

  for (let i = 0; i < pathLen - 3; i += 3) {
    const i3 = i / 3;
    const rotplane = vertexRotationPlanes[i3];

    if (rotplane === undefined) {
      // handles start frame when not closed
      miterPlanes[i] = edgeFrames[i];
      continue;
    }

    const plane = planeNew();
    miterPlanes[i3] = plane;

    plane.originX = path[i + 0];
    plane.originY = path[i + 1];
    plane.originZ = path[i + 2];

    const curFrame = edgeFrames[i3];
    const prevFrame = edgeFrames[(i3 - 1 + edgeFrames.length) % edgeFrames.length];

    if (rotplane === RotationPlane.XZ) {
      const ux0 = prevFrame.uX;
      const uy0 = prevFrame.uY;
      const uz0 = prevFrame.uZ;

      const ux1 = curFrame.uX;
      const uy1 = curFrame.uY;
      const uz1 = curFrame.uZ;

      let [miterx, mitery, miterz] = vec3Add(ux0, uy0, uz0, ux1, uy1, uz1);

      const len = 1 / (miterx * ux0 + mitery * uy0 + miterz * uz0);
      [miterx, mitery, miterz] = vec3Scale(miterx, mitery, miterz, len);

      plane.uX = miterx;
      plane.uY = mitery;
      plane.uZ = miterz;

      plane.vX = prevFrame.vX;
      plane.vY = prevFrame.vY;
      plane.vZ = prevFrame.vZ;
    } else {
      const vx0 = prevFrame.vX;
      const vy0 = prevFrame.vY;
      const vz0 = prevFrame.vZ;

      const vx1 = curFrame.vX;
      const vy1 = curFrame.vY;
      const vz1 = curFrame.vZ;

      let [miterx, mitery, miterz] = vec3Add(vx0, vy0, vz0, vx1, vy1, vz1);

      const len = 1 / (miterx * vx0 + mitery * vy0 + miterz * vz0);
      [miterx, mitery, miterz] = vec3Scale(miterx, mitery, miterz, len);

      plane.vX = miterx;
      plane.vY = mitery;
      plane.vZ = miterz;

      plane.uX = prevFrame.uX;
      plane.uY = prevFrame.uY;
      plane.uZ = prevFrame.uZ;
    }
  }

  if (!pathClosed) {
    const endPlane = planeCopy(edgeFrames[edgeFrames.length - 1]);

    endPlane.originX = path[path.length - 3];
    endPlane.originY = path[path.length - 2];
    endPlane.originZ = path[path.length - 1];

    miterPlanes.push(endPlane);
  }

  // 3. build faces between frames

  const segs = pathClosed ? miterPlanes.length : miterPlanes.length - 1;

  const faces = new Array<Face>();
  const faceTopos = new Array<FaceTopo>();

  // add end caps
  if (!pathClosed) {
    const startTags = pathSegmentFaceTags?.[0];
    const startFace = faceOrNullCopy(faceNew(profile, miterPlanes[0], startTags ? [...startTags] : undefined));
    faceOrNullReverse(startFace);
    if (startFace) {
      faces.push(startFace);
    }
    faceTopos.push(faceTopoNew());

    const endTags = pathSegmentFaceTags?.[segs - 1];
    const endFace = faceNew(profile, miterPlanes[miterPlanes.length - 1], endTags ? [...endTags] : undefined);
    faces.push(endFace);
    faceTopos.push(faceTopoNew());
  }

  for (let i = 0; i < segs; i++) {
    buildFacesBetweenEdges(
      profile,
      miterPlanes[i],
      miterPlanes[(i + 1) % miterPlanes.length],
      faces,
      faceTopos,
      pathSegmentFaceTags?.[i],
    );
  }

  return brepNew(faces, faceTopos);
}

export function extrudeSquare(x: number, y: number, z: number, height: number, squareWidth: number): Extrusion {
  const profile = rect2ByCenterWidthHeight(x, y, 2 * squareWidth, 2 * squareWidth);
  return extrudePolyLine2(profile, planeNewXY(0, 0, z), 0, 0, height);
}

export function extrudeSquareWithRotation(
  x: number,
  y: number,
  z: number,
  height: number,
  squareWidth: number,
  rotation: number,
): Extrusion {
  const profile = polyLine2Rotate(rect2ByCenterWidthHeight(x, y, 2 * squareWidth, 2 * squareWidth), rotation, x, y);
  return extrudePolyLine2(profile, planeNewXY(0, 0, z), 0, 0, height);
}

export function extrudeRectWithRotation(
  x: number,
  y: number,
  z: number,
  height: number,
  squareWidth: number,
  squareLength: number,
  rotation: number,
): Extrusion {
  const profile = polyLine2Rotate(rect2ByCenterWidthHeight(x, y, squareWidth, squareLength), rotation, x, y);
  return extrudePolyLine2(profile, planeNewXY(0, 0, z), 0, 0, height);
}

export function extrusionAABB3ByBRep(extrusion: Extrusion): AABB3 {
  const brep = extrusionToBRep(extrusion);
  return facesAABB3(brep.faces);
}
